import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { signOut, type User } from "firebase/auth";

import { clearLangSmithKey } from "@/apps/admin/api/adminOnly/langsmithKey";
import type { DenormalizedPermission } from "@/apps/admin/utilities/data";
import { auth } from "@/common/platform/firebase";

// What Authenticated keeps of Firebase's user: plain fields, so the store stays serialisable.
export type AuthenticatedUser = Pick<
  User,
  "displayName" | "email" | "emailVerified" | "photoURL" | "uid" | "isAnonymous"
>;

export type PermissionsStatus = "loading" | "loaded" | "error";

export type AuthenticationState = {
  user: AuthenticatedUser | null;
  userLoaded: boolean;
  dialogOpen: boolean;
  permissions: DenormalizedPermission[];
  permissionsStatus: PermissionsStatus;
};

type AuthenticationRoot = { authentication: AuthenticationState };

const initialState: AuthenticationState = {
  user: null,
  userLoaded: false,
  dialogOpen: false,
  permissions: [],
  permissionsStatus: "loading",
};

/** How long sign-out waits for the remembered LangSmith key to be deleted before it goes on. */
export const SIGN_OUT_KEY_WIPE_TIMEOUT_MS = 2000;

/**
 * Deletes the remembered LangSmith key (the device copy in IndexedDB) and
 * waits for it, so the page reload that follows sign-out cannot cut the delete
 * short and leave the key for the next admin. It never throws and never waits
 * longer than the timeout: a failed or slow wipe is reported (without the key)
 * and sign-out goes on. The listener in Authenticated wipes again on any
 * sign-out, which is safe to repeat.
 */
export const wipeLangSmithKeyBeforeSignOut = async (): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<"timedOut">((resolve) => {
    timer = setTimeout(() => resolve("timedOut"), SIGN_OUT_KEY_WIPE_TIMEOUT_MS);
  });
  try {
    const outcome = await Promise.race([clearLangSmithKey(), timedOut]);
    if (outcome === "timedOut") {
      Sentry.captureMessage("LangSmith key wipe timed out at sign-out");
    }
  } catch (cause) {
    Sentry.captureException(cause);
  } finally {
    clearTimeout(timer);
  }
};

export const signout = createAsyncThunk("authentication/signout", async () => {
  try {
    await wipeLangSmithKeyBeforeSignOut();
    await signOut(auth);
  } finally {
    // eslint-disable-next-line no-restricted-globals
    location.reload();
  }
});

export const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    setUser: (
      state,
      { payload: user }: PayloadAction<AuthenticatedUser | null>
    ) => {
      // Another account (or none): the previous user's permissions must not linger
      if (state.user?.uid !== user?.uid) {
        state.permissions = [];
        state.permissionsStatus = "loading";
      }
      state.user = user;
      state.userLoaded = true;
    },
    setPermissions: (
      state,
      {
        payload: { permissions },
      }: PayloadAction<{ permissions: DenormalizedPermission[] }>
    ) => {
      state.permissions = permissions;
      state.permissionsStatus = "loaded";
    },
    setPermissionsError: (state) => {
      state.permissions = [];
      state.permissionsStatus = "error";
    },
    openSigninDialog: (state) => {
      state.dialogOpen = true;
    },
    closeSigninDialog: (state) => {
      state.dialogOpen = false;
    },
  },
});

export const {
  setUser,
  setPermissions,
  setPermissionsError,
  openSigninDialog,
  closeSigninDialog,
} = authenticationSlice.actions;

export const selectUser = (state: AuthenticationRoot) =>
  state.authentication.user;
export const selectUserLoaded = (state: AuthenticationRoot) =>
  state.authentication.userLoaded;
export const selectPermissionsStatus = (state: AuthenticationRoot) =>
  state.authentication.permissionsStatus;
export const selectSignInDialogOpen = (state: AuthenticationRoot) =>
  state.authentication.dialogOpen;
export const selectPermissions = (state: AuthenticationRoot) =>
  state.authentication.permissions;

export default authenticationSlice.reducer;
